import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import Services from "../services/Services";
import "./InvitationActionPage.css";

function getStatusFromResponse(response) {
  const data = response?.data ?? response;
  if (typeof data === "boolean") return data;
  if (typeof data?.status === "boolean") return data.status;
  return false;
}

function parseQuery(search) {
  const params = new URLSearchParams(search || "");
  return {
    roomId: String(params.get("room_id") || "").trim(),
    invitationCode: String(params.get("invitation_code") || "").trim(),
    userId: String(params.get("user_id") || "").trim(),
  };
}

function buildReturnTo(location) {
  const pathname = String(location?.pathname || "").trim();
  if (!pathname.startsWith("/")) return "/";
  const search = typeof location?.search === "string" ? location.search : "";
  const hash = typeof location?.hash === "string" ? location.hash : "";
  return `${pathname}${search}${hash}`;
}

export default function InvitationActionPage({ mode = "accept" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, isAuthenticated } = useAuth();
  const { getTranslation, language, setLanguage, availableLanguages } = useLanguage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { ok: boolean, message: string }

  const { roomId, invitationCode, userId } = useMemo(
    () => parseQuery(location.search),
    [location.search]
  );
  const returnTo = useMemo(
    () => buildReturnTo(location),
    [location.pathname, location.search, location.hash]
  );
  const authRedirectState = useMemo(
    () => ({
      from: location.pathname,
      fromSearch: location.search,
      fromHash: location.hash,
      returnTo,
    }),
    [location.pathname, location.search, location.hash, returnTo]
  );

  const isAcceptMode = mode === "accept";

  const title = isAcceptMode
    ? getTranslation("memeetAcceptInvitationTitle") || "Accept Invitation"
    : getTranslation("memeetRejectInvitationTitle") || "Reject Invitation";

  const confirmText = isAcceptMode
    ? getTranslation("memeetAcceptInvitationConfirm") || "Are you sure you want to accept this invitation?"
    : getTranslation("memeetRejectInvitationConfirm") || "Are you sure you want to reject this invitation?";
  const authRequiredText = getTranslation("memeetInvitationLoginRequired")
    || "To accept or reject this invitation, please log in first.";

  const handleGoToLogin = () => {
    navigate("/login", { state: authRedirectState });
  };

  const handleGoToRegister = () => {
    navigate("/register", { state: authRedirectState });
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      handleGoToLogin();
      return;
    }
    if (!invitationCode || isSubmitting) return;

    setIsSubmitting(true);
    setResult(null);

    try {
      const authToken = token || localStorage.getItem("token") || "";
      if (!authToken) {
        handleGoToLogin();
        return;
      }
      const response = isAcceptMode
        ? await Services.MeMeetAcceptInvitation(authToken, { invitationCode })
        : await Services.MeMeetRejectInvitation(authToken, { invitationCode });

      const ok = getStatusFromResponse(response);
      if (ok) {
        setResult({
          ok: true,
          message: isAcceptMode
            ? getTranslation("memeetAcceptInvitationSuccess") || "Invitation accepted successfully."
            : getTranslation("memeetRejectInvitationSuccess") || "Invitation rejected successfully.",
        });
      } else {
        setResult({
          ok: false,
          message: getTranslation("somethingWentWrong") || "Something went wrong.",
        });
      }
    } catch {
      setResult({
        ok: false,
        message: getTranslation("somethingWentWrong") || "Something went wrong.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenRoom = () => {
    if (!roomId) {
      navigate("/", { replace: true });
      return;
    }
    if (isAuthenticated) {
      navigate(`/memeet/${roomId}`, { replace: true });
      return;
    }
    navigate(`/login/${roomId}`, { replace: true });
  };

  const hasInvalidQuery = !invitationCode;

  return (
    <div className="invitation-action-page">
      <header className="invitation-action-header">
        <button
          className="invitation-action-brand"
          type="button"
          onClick={() => navigate("/", { replace: true })}
        >
          MeMeet
        </button>
        <div className="invitation-action-header-actions">
          <div className="invitation-action-language-selector">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="invitation-action-language-select"
              aria-label={getTranslation("memeetLanguage") || "Language"}
            >
              {availableLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>
          {/* {!isAuthenticated ? (
            <>
              <button className="invitation-btn secondary compact" type="button" onClick={handleGoToLogin}>
                {getTranslation("login") || "Login"}
              </button>
              <button className="invitation-btn primary compact" type="button" onClick={handleGoToRegister}>
                {getTranslation("signup") || "Sign Up"}
              </button>
            </>
          ) : null} */}
        </div>
      </header>

      <main className="invitation-action-main">
        <div className="invitation-action-card">
          <h1>{title}</h1>

          {hasInvalidQuery ? (
            <div className="invitation-action-error">
              {getTranslation("memeetInvalidInvitationLink") || "Invitation link is invalid."}
            </div>
          ) : null}

          {!isAuthenticated && !hasInvalidQuery ? (
            <>
              <div className="invitation-action-info">{authRequiredText}</div>
              <div className="invitation-action-buttons">
                <button className="invitation-btn secondary" type="button" onClick={handleGoToLogin}>
                  {getTranslation("login") || "Login"}
                </button>
                <button className="invitation-btn primary" type="button" onClick={handleGoToRegister}>
                  {getTranslation("signup") || "Sign Up"}
                </button>
              </div>
            </>
          ) : null}

          {!result && !hasInvalidQuery && isAuthenticated ? (
            <>
              <p>{confirmText}</p>
              {roomId ? <div className="invitation-action-meta">room_id: {roomId}</div> : null}
              {!isAcceptMode && userId ? <div className="invitation-action-meta">user_id: {userId}</div> : null}

              <div className="invitation-action-buttons">
                <button
                  className="invitation-btn secondary"
                  type="button"
                  onClick={() => navigate("/", { replace: true })}
                  disabled={isSubmitting}
                >
                  {getTranslation("memeetCancel") || "Cancel"}
                </button>
                <button
                  className={`invitation-btn ${isAcceptMode ? "primary" : "danger"}`}
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? getTranslation("memeetLoading") || "Loading..."
                    : getTranslation("memeetConfirm") || "Confirm"}
                </button>
              </div>
            </>
          ) : null}

          {result ? (
            <>
              <div className={result.ok ? "invitation-action-success" : "invitation-action-error"}>
                {result.message}
              </div>
              <div className="invitation-action-buttons">
                {result.ok && roomId ? (
                  <button className="invitation-btn primary" type="button" onClick={handleOpenRoom}>
                    {getTranslation("memeetOpenRoom") || "Open Room"}
                  </button>
                ) : null}
                <button
                  className="invitation-btn secondary"
                  type="button"
                  onClick={() => navigate("/", { replace: true })}
                >
                  {getTranslation("memeetGoHome") || "Go Home"}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
}
